import Loadable from '@app/utils/Loadable';
import validationSystemServices from '@mortee/services/validationSystemServices';
import useLoadable from '@app/hooks/loadable/useLoadable';
import { useDebounce } from '@app/hooks/useDebounce';
import { isSwiftCodeValid } from '@app/domain/accountDetailsValidators';

// Resolves the DB bank name for a full SWIFT code. Mirrors useBankNameByCountryCodeAndBankCode:
// debounced, and only queries when the value is a valid full (11-char, with-suffix) SWIFT. Returns
// null while loading / when the value is not a full SWIFT / when the DB has no name (the service
// suppresses the 404). Pass undefined (e.g. for non-SWIFT rows) to no-op.
export default function useBankNameBySwiftCode(swift: string | undefined): Loadable<string | null> {
  const debouncedSwift = useDebounce(swift?.trim().toUpperCase(), 400);

  const [result] = useLoadable(async () => {
    if (!debouncedSwift || !isSwiftCodeValid(debouncedSwift, true)) {
      return null;
    }

    // A SWIFT with no DB match returns 404 (notification already suppressed by the service). Swallow
    // it and return null — "no bank name" is a normal outcome, not an error to surface. Without this
    // the rejected promise bubbles up as an "Uncaught (in promise)" for every unmatched SWIFT.
    try {
      return await validationSystemServices.getBankNameBySwiftCode(debouncedSwift);
    } catch {
      return null;
    }
  }, [debouncedSwift]);

  return result;
}
